/**
 * Tiny fetch wrapper that attaches the Clerk session JWT automatically.
 *
 * Consumers call `useApi()` inside React to get a bound instance.
 */

import { useAuth } from '@clerk/react';
import { useCallback, useMemo } from 'react';

const BASE = (import.meta.env.VITE_API_BASE as string | undefined) ?? '';

export type ApiError = { error: string; detail?: string };

/** Reject a promise after `ms` milliseconds. Used to escape hung Clerk calls. */
function withTimeout<T>(p: Promise<T>, ms: number, message: string): Promise<T> {
  return new Promise((resolve, reject) => {
    const t = setTimeout(() => reject(new Error(message)), ms);
    p.then((v) => { clearTimeout(t); resolve(v); })
     .catch((e) => { clearTimeout(t); reject(e); });
  });
}

export class ApiClient {
  constructor(private readonly getToken: () => Promise<string | null>) {}

  async request<T>(path: string, init: RequestInit = {}): Promise<T> {
    // Timeout any request that hangs — usually means the Worker is wedged
    // on a Clerk JWKS fetch or its proxy dropped the connection.
    const ctrl = new AbortController();
    const timeout = setTimeout(() => ctrl.abort(), 10_000);

    try {
      const token = await withTimeout(
        this.getToken(),
        5_000,
        'Clerk getToken() timed out',
      );
      const headers = new Headers(init.headers);
      headers.set('Accept', 'application/json');
      if (init.body && !headers.has('Content-Type')) headers.set('Content-Type', 'application/json');
      if (token) headers.set('Authorization', `Bearer ${token}`);

      const res = await fetch(`${BASE}${path}`, { ...init, headers, signal: ctrl.signal });
      const text = await res.text();
      const data = text ? JSON.parse(text) : null;
      if (!res.ok) {
        const err = new Error(
          (data as ApiError)?.error ?? `HTTP ${res.status}`,
        ) as Error & { status: number; data: unknown };
        err.status = res.status;
        err.data = data;
        throw err;
      }
      return data as T;
    } catch (err) {
      if ((err as DOMException)?.name === 'AbortError') {
        const e = new Error('Worker did not respond within 10s (is wrangler dev running on :8787?)') as
          Error & { status: number; data: unknown };
        e.status = 504;
        e.data = { error: 'worker_timeout', detail: 'Fetch aborted by the client.' };
        throw e;
      }
      throw err;
    } finally {
      clearTimeout(timeout);
    }
  }

  get<T>(path: string) { return this.request<T>(path, { method: 'GET' }); }
  delete<T>(path: string) { return this.request<T>(path, { method: 'DELETE' }); }
  post<T>(path: string, body?: unknown) {
    return this.request<T>(path, { method: 'POST', body: body ? JSON.stringify(body) : undefined });
  }
  patch<T>(path: string, body?: unknown) {
    return this.request<T>(path, { method: 'PATCH', body: body ? JSON.stringify(body) : undefined });
  }
}

export function useApi(): ApiClient {
  const { getToken } = useAuth();
  const fetchToken = useCallback(() => getToken(), [getToken]);
  return useMemo(() => new ApiClient(fetchToken), [fetchToken]);
}

// Shared response types (mirror Worker routes)
export interface MeResponse {
  userId: string;
  clerkOrgId: string | null;
  orgRole: 'org:admin' | 'org:agent' | 'org:viewer' | null;
  org: {
    id: string;
    name: string;
    slug: string;
    email_prefix: string | null;
    ai_auto_send_threshold: number;
    ai_signature: string | null;
    ai_tone: string;
    ai_policy: string | null;
  } | null;
}

export interface Ticket {
  id: string;
  org_id: string;
  agent_instance: string;
  status: 'open' | 'awaiting_approval' | 'resolved' | 'spam';
  subject: string | null;
  from_email: string;
  last_activity: number;
  ai_confidence: number | null;
  ai_category: string | null;
  priority: 'low' | 'normal' | 'high' | 'urgent';
  created_at: number;
}

export interface ToolConfig {
  id: string;
  tool_id: string;
  source: 'composio' | 'custom_http' | 'builtin' | 'mcp';
  enabled: number;
  needs_approval: number;
  config_json: string | null;
}

export interface Connection {
  toolkit: string;           // lower-case slug
  status: string;            // 'active' | 'initiated' | 'failed' | 'expired' | ...
}

export interface CatalogToolkit {
  slug: string;
  name: string;
  description: string | null;
  logo: string | null;
  toolsCount: number;
  categories: string[];
  authSchemes: string[];
}
